"use client";

import React from "react";
import { Button, Flex, Typography } from "antd";
import { ComponentProps } from "./types";
import { MODAL_COMPONENT_SIZE, RESCHEDULE_MAIN } from "./consts";
import { LeftOutlined } from "@ant-design/icons";
import { useApp } from "@/lib/hooks/useAppointment";
import dayjs from "dayjs";
import { DISPLAY_DATE_FORMAT } from "@utils/core";
import type { AppointmentHistory } from "@openings/api-client";
import { useLocale } from "@/lib/i18n";
import styles from "./appointmentFlow.module.css";

/** Match the API's `toLocalDateTime` display format (e.g. "Feb 20, 2026 10:00 AM") */
const formatLocalDatetime = (date?: string, time?: string): string => {
  if (!date || !time) return "";
  return dayjs(`${date} ${time}`).format(`${DISPLAY_DATE_FORMAT} h:mm A`);
};

const RescheduleList: React.FC<ComponentProps> = ({
  state,
  setState,
  onRescheduleAppointment,
}) => {
  const { t } = useLocale();
  const { appointmentForm } = useApp();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = React.useState<
    AppointmentHistory["upcomingAppointments"][number] | undefined
  >();

  // Get from router state
  const upcomingAppointments =
    state.state.upcomingAppointments ??
    ([] as AppointmentHistory["upcomingAppointments"]);

  const getAppointmentId = (
    appointment: AppointmentHistory["upcomingAppointments"][number],
  ) => appointment.appointmentId ?? appointment.id;

  const getFormattedDatetime = (
    appointment: AppointmentHistory["upcomingAppointments"][number],
  ) =>
    appointment.formattedDatetime ??
    formatLocalDatetime(appointment.date, appointment.time);

  const handleReschedule = () => {
    if (appointmentToCancel) {
      const previousAppointmentId = getAppointmentId(appointmentToCancel);
      if (!previousAppointmentId) return;
      setSubmitting(true);
      onRescheduleAppointment({
        previousAppointmentId,
        paymentMethodId: state.state.paymentMethodId,
        verificationCode: state.state.rescheduleVerificationCode,
        errorCallback: () => {
          setSubmitting(false);
        },
      });
    }
  };

  const selectAppointmentToCancel = (
    appointment: AppointmentHistory["upcomingAppointments"][number],
  ) => setAppointmentToCancel(appointment);

  if (isSubmitting) {
    return <div>{t.processingReschedule}</div>;
  }
  return (
    <>
      {appointmentToCancel ? (
        <RescheduleReview
          isSubmitting={isSubmitting}
          from={getFormattedDatetime(appointmentToCancel)}
          to={
            appointmentForm
              ? formatLocalDatetime(appointmentForm.date, appointmentForm.time)
              : ""
          }
          onClick={handleReschedule}
        />
      ) : (
        <UpcomingList
          onBack={() =>
            setState({ route: RESCHEDULE_MAIN, state: state.state })
          }
          upcomingAppointments={upcomingAppointments}
          onClick={selectAppointmentToCancel}
        />
      )}
    </>
  );
};

const RescheduleReview = ({
  from,
  to,
  onClick,
  isSubmitting,
}: {
  from: string;
  to: string;
  onClick: () => void;
  isSubmitting: boolean;
}) => {
  const { t } = useLocale();
  return (
    <div className={styles.stepContainer}>
      <Typography.Paragraph className={styles.prompt}>
        {t.reschedulingFrom(from, to)}
      </Typography.Paragraph>
      <Button
        size={MODAL_COMPONENT_SIZE}
        loading={isSubmitting}
        onClick={onClick}
        type="primary"
        block
      >
        {t.confirm}
      </Button>
    </div>
  );
};

const UpcomingList = ({
  upcomingAppointments,
  onClick,
  onBack,
}: {
  upcomingAppointments: AppointmentHistory["upcomingAppointments"];
  onClick: (
    appointment: AppointmentHistory["upcomingAppointments"][number],
  ) => void;
  onBack: () => void;
}) => {
  const { t } = useLocale();
  return (
    <div className={styles.stepContainer}>
      <Typography.Paragraph className={styles.prompt}>
        {t.selectToReschedule}
      </Typography.Paragraph>

      <Flex vertical gap="small" style={{ width: "100%" }}>
        {upcomingAppointments.map((appointment) => (
          <Button
            key={appointment.appointmentId ?? appointment.id}
            block
            size={MODAL_COMPONENT_SIZE}
            onClick={() => onClick(appointment)}
          >
            {appointment.formattedDatetime ??
              formatLocalDatetime(appointment.date, appointment.time)}
          </Button>
        ))}
      </Flex>
      <div className={styles.actionRow}>
        <Button
          color="default"
          variant="filled"
          size={MODAL_COMPONENT_SIZE}
          onClick={onBack}
          icon={<LeftOutlined />}
          block
        >
          {t.back}
        </Button>
      </div>
    </div>
  );
};

export default RescheduleList;
